import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  BookOpen,
  Trophy,
  Phone,
  Clock,
  TrendingUp,
  CheckCircle,
} from 'lucide-react';
import api from '../../services/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface DashboardData {
  user: any;
  stats: {
    progression_globale: number;
    modules_termines: number;
    modules_total: number;
    quiz_reussis: number;
    quiz_total: number;
    meilleur_score_quiz: number | null;
    score_moyen_simulations: number | null;
    temps_total_formation: number;
  };
  current_promotion: any;
  recent_modules: any[];
  recent_quiz_results: any[];
  recent_simulations: any[];
}

interface Promotion {
  id: number;
  nom: string;
  product: { id: number; nom: string };
}

interface SimulationScenario {
  id: number;
  nom: string;
  description: string;
  ordre: number;
}

interface CallSimulation {
  id: number;
  scenario_id: number;
  score: number | null;
  duration: number;
  feedback: string | null;
  created_at: string;
}

export default function UserProfile() {
  const { userId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const highlightPromotionId = searchParams.get('promotionId');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [availablePromotions, setAvailablePromotions] = useState<Promotion[]>([]);
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);
  const [scenarios, setScenarios] = useState<SimulationScenario[]>([]);
  const [history, setHistory] = useState<CallSimulation[]>([]);
  const [loadingSims, setLoadingSims] = useState(true);

  const getAverageScore = () => {
    const scores = history.filter(h => h.score !== null).map(h => h.score!);
    if (scores.length === 0) return 0;
    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/stats/user/${userId}/dashboard`, {
          params: highlightPromotionId ? { promotion_id: highlightPromotionId } : {}
        });
        setData(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [userId, highlightPromotionId]);



  useEffect(() => {
    const loadPromos = async () => {
      try {
        const res = await api.get(`/formateur/user/${userId}/promotions`);
        const promos: { registered: Promotion[]; available: Promotion[] } = res.data;
        setPromotions(promos.registered);
        setAvailablePromotions(promos.available);
        if (promos.registered.length > 0) {
          const selected = highlightPromotionId
            ? promos.registered.find(p => p.id === Number(highlightPromotionId)) || promos.registered[0]
            : promos.registered[0];
          loadSimulationData(selected);
        } else {
          setLoadingSims(false);
        }
      } catch (e) {
        console.error(e);
        setLoadingSims(false);
      }
    };
    loadPromos();
  }, [userId, highlightPromotionId]);

  const loadSimulationData = async (promo: Promotion) => {
    try {
      setLoadingSims(true);
      const [scenariosRes, dashboardRes] = await Promise.all([
        api.get(`/products/${promo.product.id}/simulations`),
        api.get(`/stats/user/${userId}/dashboard`, {
          params: { promotion_id: promo.id }
        })
      ]);
      setCurrentPromotion(promo);
      setScenarios(scenariosRes.data);
      setHistory(dashboardRes.data.recent_simulations);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingSims(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!data) {
    return <div>Utilisateur introuvable</div>;
  }

  const { user, stats, current_promotion, recent_quiz_results, recent_modules } = data;

  const completedModules = recent_modules.filter(
    (m) => m.status === 'completed'
  );

  const pieData = [
    { name: 'Terminé', value: stats.modules_termines },
    { name: 'Restant', value: stats.modules_total - stats.modules_termines }
  ];

  const COLORS = ['#004E89', '#E5E7EB'];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Profil de {user.prenom} {user.nom}
        </h1>
        {current_promotion && (
          <p className="mt-2 text-gray-600">
            Formation en cours : <span className="font-semibold">{current_promotion.product.nom}</span>
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Progression globale</p>
              <p className="text-2xl font-bold text-gray-900">{stats.progression_globale.toFixed(0)}%</p>
            </div>
            <TrendingUp className="h-8 w-8 text-primary" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Modules terminés</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.modules_termines}/{stats.modules_total}
              </p>
            </div>
            <BookOpen className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Meilleur score quiz</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.meilleur_score_quiz ? `${stats.meilleur_score_quiz}%` : '-'}
              </p>
            </div>
            <Trophy className="h-8 w-8 text-yellow-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Temps total</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.floor(stats.temps_total_formation / 60)}h {stats.temps_total_formation % 60}m
              </p>
            </div>
            <Clock className="h-8 w-8 text-blue-500" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Progression des modules</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-center mb-6">Formations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h2 className="text-2xl font-bold mb-2">Formations inscrites:</h2>
            <ul className="space-y-2">
              {promotions.map((promo) => (
                <li
                  key={promo.id}
                  className={promo.id === Number(highlightPromotionId) ? 'font-bold' : ''}
                >
                  {promo.product.nom} - {promo.nom}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-bold mb-2">Formations non inscrites:</h2>
            <ul className="space-y-2">
              {availablePromotions.map((promo) => (
                <li key={promo.id}>
                  {promo.product.nom} - {promo.nom}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Modules terminés</h2>
        {completedModules.length > 0 ? (
          <div className="space-y-3">
            {completedModules.map((module) => (
              <div key={module.id} className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                <span className="font-medium">{module.titre}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">Aucun module terminé pour cette formation.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Résultats de quiz récents</h2>
        {recent_quiz_results.length > 0 ? (
          <div className="space-y-3">
            {recent_quiz_results.map((result) => (
              <div key={result.id} className="flex items-center justify-between py-2">
                <div className="flex items-center">
                  <Trophy className={`h-5 w-5 mr-3 ${result.passed ? 'text-green-500' : 'text-red-500'}`} />
                  <span className="font-medium">Quiz du {new Date(result.taken_at).toLocaleDateString()}</span>
                </div>
                <span className={`text-sm font-semibold ${result.passed ? 'text-green-600' : 'text-red-600'}`}>{result.score_percentage}%</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">L’utilisateur n’a pas encore passé de quiz pour cette formation.</p>
        )}
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold mb-4">Simulations</h2>

        {promotions.length > 1 && (
          <div className="mb-6">
            <label htmlFor="promotion" className="block text-sm font-medium text-gray-700">
              Promotion
            </label>
            <select
              id="promotion"
              value={currentPromotion?.id ?? ''}
              onChange={(e) => {
                const promo = promotions.find(p => p.id === Number(e.target.value));
                if (promo) {
                  loadSimulationData(promo);
                }
              }}
              className="mt-2 p-2 border rounded w-full"
            >
              {promotions.map((promo) => (
                <option key={promo.id} value={promo.id}>{promo.nom}</option>
              ))}
            </select>
          </div>
        )}

        {loadingSims ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Simulations réalisées</p>
                    <p className="text-2xl font-bold text-gray-900">{history.length}</p>
                  </div>
                  <Phone className="h-8 w-8 text-primary" />
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Score moyen</p>
                    <p className="text-2xl font-bold text-gray-900">{getAverageScore()}%</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Dernière simulation</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {history.length > 0 ? new Date(history[0].created_at).toLocaleDateString() : '-'}
                    </p>
                  </div>
                  <Clock className="h-8 w-8 text-blue-500" />
                </div>
              </div>
            </div>

            {history.length > 0 && (
              <div>
                <h3 className="text-md font-semibold mb-4">Historique des simulations</h3>
                <div className="space-y-4">
                  {history.map((sim) => {
                    const scenario = scenarios.find(s => s.id === sim.scenario_id);
                    const startDate = new Date(sim.created_at);
                    const endDate = new Date(startDate.getTime() + sim.duration * 1000);
                    return (
                      <div key={sim.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900">{scenario?.nom || 'Simulation'}</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {startDate.toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className="text-sm text-gray-600">Fin : {endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</p>

                            {sim.score !== null ? (
                              <>
                                <div className="mt-3 flex items-center">
                                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                                    <div
                                      className={`h-2 rounded-full ${
                                        sim.score >= 80 ? 'bg-green-500' : sim.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${sim.score}%` }}
                                    />
                                  </div>
                                  <span className="ml-3 text-sm font-medium">{sim.score}%</span>
                                </div>

                                {sim.feedback && (
                                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{sim.feedback}</p>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="mt-3 p-3 bg-yellow-50 rounded-lg">
                                <p className="text-sm text-yellow-800">
                                  Analyse en cours... Les résultats apparaîtront dans quelques minutes.
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}